<template>
  <div>
    <Popup :open="popupOpen" :message="popupMessage" :onDone="closePopup" />
    <ErrorPopup :open="errorPopupOpen" :message="popupErrorMessage" :title="errorPopupTitle"
      :onDone="closeErrorPopup" />
    <YesNoPopup :open="yesNoPopupOpen" :title="t('documentPreview.areYouSure')"
      :subtitle="t('documentPreview.onceYouContinue')" :onYes="handleYes" :onNo="handleNo" />
    <div v-if="isLoading" class="centered">
      <div class="horizontal-loader">
        <div class="loader-bar"></div>
      </div>
      <div class="loading-message">{{ messages }}</div>
    </div>
    <div v-else class="document-preview-root">
      <!-- Header -->
      <div class="header-row">
        <div class="header-left">
          <!-- Editable Class Name for Bedrock flow -->
          <div v-if="isEditMode && props.selectedTenantUsesBedrock">
            <div class="header-label">{{ t('documentPreview.classifiedAs') }}</div>
            <div class="header-value">
              <input v-if="isEditingClass" ref="classInput" v-model="editedClassName" @blur="saveClassEdit"
                @keyup.enter="saveClassEdit" @keyup.esc="cancelClassEdit" class="edit-input"
                style="width: 200px; padding: 4px 8px; border: 1px solid #ccc; border-radius: 4px" />
              <div v-else @click="startEditingClass"
                style="cursor: pointer; display: flex; align-items: center; gap: 8px">
                <span>{{ formatClassName(props.blueprintSchema?.class || props.blueprintName) }}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </div>
            </div>
          </div>
          <div class="vertical-divider" v-if="isEditMode && props.selectedTenantUsesBedrock"></div>
          <div>
            <div class="header-label">{{ t('documentPreview.sourceDocument') }}</div>
            <div class="header-value">{{ selectedFile?.name ?? '' }}</div>
          </div>
        </div>
        <button class="blueprint-btn" @click="handleEditAndFinish">
          {{ (isSchemaEdited && props.selectedTenantUsesBedrock) ? t('documentPreview.updateDocument') :
            t('documentPreview.next') }}
        </button>
      </div>

      <!-- Panels -->
      <div class="panel-group">
        <!-- Left Panel -->
        <div class="panel left-panel">
          <div class="panel-header">
            <div class="panel-title">{{ t('documentPreview.extraction') }}</div>
            <!-- Only show Add New Field button if NOT using Textract (Textract is read-only) -->
            <button class="add-btn-underline" v-if="isEditMode && !props.selectedTenantUsesTextract"
              @click="showAdd = true">{{ t('documentPreview.addNewField') }}</button>
          </div>

          <!-- Tabs for Textract AnalyzeExpense with line items and tables -->
          <div v-if="props.selectedTenantUsesTextract && (tables.length > 0 || lineItems.length > 0)"
            class="tabs-container">
            <div class="tabs-header">
              <button @click="activeTab = 'keyValues'" :class="['tab-btn', { active: activeTab === 'keyValues' }]">
                {{ t('documentPreview.keyValues') || 'Key Values' }}
              </button>
              <button @click="activeTab = 'lineItems'" :class="['tab-btn', { active: activeTab === 'lineItems' }]">
                {{ t('documentPreview.lineItems') || 'Line Items' }}
              </button>
            </div>

            <!-- Key Values Tab -->
            <div v-if="activeTab === 'keyValues'" class="tab-content">
              <BlueprintEditor :blueprintSchema="blueprintSchema" @update="onBlueprintUpdate" @focusField="focusField"
                :isEditMode="props.isEditMode" :isTextractReadOnly="props.selectedTenantUsesTextract" />
            </div>

            <!-- Line Items Tab -->
            <div v-if="activeTab === 'lineItems'" class="tab-content">
              <!-- Multiple Tables - Render All Below Each Other -->
              <div v-if="tables.length > 0" class="tables-container">
                <div v-for="(table, tableIdx) in tables" :key="tableIdx" class="line-items-table">
                  <div class="table-header">
                    <h4>{{ table.tableName }}</h4>
                    <span class="table-info">Page {{ table.pageNumber }} | {{ table.lineItems.length }} items</span>
                  </div>
                  <table class="line-items-grid">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th v-for="columnName in getTableColumns(table)" :key="columnName">
                          {{ formatColumnName(columnName) }}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(item, idx) in table.lineItems" :key="idx" class="line-item-row">
                        <td class="line-number">{{ item.lineNumber }}</td>
                        <td v-for="columnName in getTableColumns(table)" :key="columnName" class="line-item-cell"
                          @click="highlightLineItemField(item, columnName)"
                          :class="{ highlighted: selectedLineItemField?.columnName === columnName && selectedLineItemField?.lineNumber === item.lineNumber }">
                          {{ item.fields[columnName]?.value || '—' }}
                          <span v-if="item.fields[columnName]?.confidence" class="confidence-badge">
                            {{ Math.round(item.fields[columnName].confidence) }}%
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              <!-- Legacy flat lineItems fallback -->
              <div v-else-if="lineItems.length > 0" class="line-items-table">
                <table class="line-items-grid">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th v-for="columnName in dynamicColumns" :key="columnName">
                        {{ formatColumnName(columnName) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in lineItems" :key="idx" class="line-item-row">
                      <td class="line-number">{{ item.lineNumber }}</td>
                      <td v-for="columnName in dynamicColumns" :key="columnName" class="line-item-cell"
                        @click="highlightLineItemField(item, columnName)"
                        :class="{ highlighted: selectedLineItemField?.columnName === columnName && selectedLineItemField?.lineNumber === item.lineNumber }">
                        {{ item.fields[columnName]?.value || '—' }}
                        <span v-if="item.fields[columnName]?.confidence" class="confidence-badge">
                          {{ Math.round(item.fields[columnName].confidence) }}%
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="empty-state">
                {{ t('documentPreview.noLineItems') || 'No line items found' }}
              </div>
            </div>
          </div>

          <!-- Default view when no line items or non-Textract -->
          <div v-else>
            <div v-if="showAdd" class="add-form-card match-edit-ui">
              <div class="add-form-label">{{ t('documentPreview.label') }}</div>
              <input class="add-form-input" v-model="addField.fieldName"
                :placeholder="t('documentPreview.fieldName')" />
              <div class="add-form-label">{{ t('documentPreview.instruction') }}</div>
              <textarea class="add-form-input" v-model="addField.instruction"
                :placeholder="t('documentPreview.instruction')" rows="2" style="resize: vertical" />
              <div class="add-form-actions add-form-actions-bottom">
                <button class="icon-btn" @click="handleAddNewField" :title="t('documentPreview.save')">
                  <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M9 16.2l-3.5-3.5a1 1 0 1 1 1.4-1.4l2.1 2.09 5.59-5.59a1 1 0 1 1 1.41 1.41l-6.3 6.29a1 1 0 0 1-1.4 0z" />
                  </svg>
                </button>
                <button class="icon-btn" @click="handleCancelAddNewField" :title="t('documentPreview.cancel')">
                  <svg class="icon" viewBox="0 0 24 24">
                    <path fill="currentColor"
                      d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                  </svg>
                </button>
              </div>
            </div>
            <BlueprintEditor :blueprintSchema="blueprintSchema" @update="onBlueprintUpdate" @focusField="focusField"
              :isEditMode="props.isEditMode" :isTextractReadOnly="props.selectedTenantUsesTextract" />
          </div>
        </div>

        <!-- Center Panel: Replaced with ImageHighlighter -->
        <div class="panel center-panel">
          <!-- <div class="zoom-bar">
            <button class="icon-btn" @click="prevPage" :disabled="currentPage <= 0">⬅</button>
            <span>Page {{ currentPage + 1 }} of {{ totalPages }}</span>
            <button class="icon-btn" @click="nextPage" :disabled="currentPage >= totalPages - 1">➡</button>
          </div> -->

          <ImageHighlighter :textData="pageBoundingBoxes" :imageSrc="currentImageSrc" :key="currentPage"
            :focusedFieldKey="selectedFieldKey" />
        </div>

        <!-- Right Panel -->
        <div class="panel right-panel">
          <div class="panel-title" style="text-align: center">{{ t('documentPreview.pages') }} ({{ docArray.length }})
          </div>
          <div v-for="(doc, index) in docArray" :key="doc.id"
            :class="['doc-page-card', { selected: selectedDocument === index }]" @click="setSelectedDocument(index)">
            <img :src="doc.imageUrl" alt="Page Thumbnail" class="doc-page-thumbnail" />
            <div class="doc-page-caption">{{ t('documentPreview.page') }} {{ index + 1 }}</div>
          </div>
          <div class="panel-footer">{{ t('documentPreview.page') }} {{ selectedDocument + 1 }} {{
            t('documentPreview.of') }} {{ docArray.length }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, toRaw, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import Popup from './Popup.vue'
import YesNoPopup from './YesNoPopup.vue'
import BlueprintEditor from './BlueprintEditor.vue'
import ImageHighlighter from './ImageHighlighter.vue'
import { pollForStatus, mergeExplainabilityInfo } from '../utils/utils'
import ErrorPopup from './ErrorPopup.vue'
const projectArn = 'arn:aws:bedrock:us-east-1:692859933965:data-automation-project/79d3fa7dfb1e'
const baseURL = 'http://localhost:8000'
import { post, get, put } from '../../../utils/api'
import { useRouter, useRoute } from 'vue-router'

const { t } = useI18n()

const props = defineProps({
  s3DocumentUrl: Object,
  blueprintSchema: Object,
  selectedFile: Object,
  setBlueprintSchema: Function,
  setFinalResult: Function,
  blueprintArn: String,
  finalResult: Object,
  docArray: Array,
  isEditMode: Boolean,
  blueprintName: String,
  classSelectionMode: String,
  selectedTenantUsesBedrock: Boolean,
  selectedTenantUsesTextract: Boolean,
})
const router = useRouter()
const route = useRoute()
const isLoading = ref(false)
const messages = ref('')
const popupOpen = ref(false)
const yesNoPopupOpen = ref(false)
const popupMessage = ref('')
const popupOnDone = ref(() => { })
const isSchemaEdited = ref(false)
const showAdd = ref(false)
const addField = ref({ fieldName: '', instruction: '' })
const selectedDocument = ref(0)
const currentPage = ref(0)
let pendingAction = null
const emit = defineEmits(['complete', 'displayNameChange'])
const explainabilityInfo = ref([])
const isEditingClass = ref(false)
const editedClassName = ref('')
const originalClassName = ref('') // Track original class for change detection
const classInput = ref(null)
// Display name editing state
const isEditingDisplayName = ref(false)
const editedDisplayName = ref('')
const displayName = ref(props.blueprintName)
const displayNameInput = ref(null)
// error Popup state
const errorPopupOpen = ref(false)
const popupErrorMessage = ref('')
const errorPopupTitle = ref('')
// Store invocation ARN at component level
const storedInvocationArn = ref(null)

// Line items and tables state
const lineItems = ref([])
const tables = ref([]) // For separated tables
const activeTab = ref('keyValues')
const dynamicColumns = ref([])
const columnOrder = ref([])
const selectedLineItemField = ref(null)

watch(
  () => props.finalResult,
  (val) => {
    if (val?.explainability_info) {
      // Transform Textract format to Bedrock format if needed
      const explainability = val.explainability_info

      console.log('🔍 DocumentPreview: Processing explainability_info')
      console.log('   Format check:', {
        isArray: Array.isArray(explainability),
        hasFirstElement: !!explainability[0],
        firstElementIsArray: Array.isArray(explainability[0]),
        firstElementType: typeof explainability[0],
        sampleData: Array.isArray(explainability[0]) ? explainability[0][0] : Object.keys(explainability[0] || {}).slice(0, 3),
      })

      // Check if this is Textract format: array of arrays with flat field objects
      if (Array.isArray(explainability) && explainability[0] && Array.isArray(explainability[0])) {
        console.log('🔄 Transforming Textract format to Bedrock format')
        // Transform Textract format to Bedrock format
        const transformed = {}
        explainability[0].forEach((item) => {
          if (item.field && item.boundingBox) {
            if (!transformed[item.field]) {
              transformed[item.field] = { geometry: [] }
            }
            // Normalize bounding box to lowercase (Textract uses PascalCase, Bedrock uses lowercase)
            const normalizedBoundingBox = {
              left: item.boundingBox.Left ?? item.boundingBox.left ?? 0,
              top: item.boundingBox.Top ?? item.boundingBox.top ?? 0,
              width: item.boundingBox.Width ?? item.boundingBox.width ?? 0,
              height: item.boundingBox.Height ?? item.boundingBox.height ?? 0,
            }
            transformed[item.field].geometry.push({
              page: item.page,
              boundingBox: normalizedBoundingBox,
              confidence: item.confidence,
            })
          }
        })
        console.log('✅ Transformed to Bedrock format. Fields:', Object.keys(transformed))
        explainabilityInfo.value = [transformed]
      } else {
        console.log('✅ Using Bedrock format as-is')
        // Already in Bedrock format
        explainabilityInfo.value = explainability
      }
    }

    // Load tables if available (for AnalyzeExpense with multiple tables)
    if (val?.tables && Array.isArray(val.tables) && val.tables.length > 0) {
      tables.value = val.tables
      console.log('📊 Loaded tables:', tables.value.length, 'tables')
      console.log('   Table breakdown:', tables.value.map(t => `${t.tableName} (${t.lineItems.length} items)`))

      // For multiple tables, we'll compute columns per table in the template
      // Just set a default from first table for reference
      const firstTable = tables.value[0]
      if (firstTable?.lineItems[0]?.fields) {
        columnOrder.value = Object.keys(firstTable.lineItems[0].fields)
        dynamicColumns.value = columnOrder.value
      }
    } else if (val?.lineItems && Array.isArray(val.lineItems) && val.lineItems.length > 0) {
      // Fallback to flat lineItems for backward compatibility
      lineItems.value = val.lineItems
      console.log('📊 Loaded line items (legacy format):', lineItems.value.length)
      // Preserve column order as received from Textract
      if (val.lineItems[0]?.fields) {
        // Use Object.keys to maintain insertion order (as received from Textract)
        columnOrder.value = Object.keys(val.lineItems[0].fields)
        dynamicColumns.value = columnOrder.value
      }
    }
  },
  { immediate: true, deep: true }
) // To be populated from Bedrock JSON output

// Watch for blueprint schema class changes
watch(
  () => props.blueprintSchema?.class,
  (newClass, oldClass) => {
    if (newClass !== oldClass) {
      const formattedClass = formatClassName(newClass || props.blueprintName)
      console.log('DocumentPreview: Blueprint class changed:', oldClass, '→', newClass, 'formatted:', formattedClass)
      // Emit class name change for parent components to update
      emit('displayNameChange', formattedClass)
    }
  },
  { immediate: true }
)

const selectedFieldKey = ref(null)
function showErrorPopup(msg, title) {
  popupErrorMessage.value = msg
  errorPopupTitle.value = title
  errorPopupOpen.value = true
}
function startEditingClass() {
  isEditingClass.value = true

  // Store original class value for change comparison
  const rawClass = props.blueprintSchema?.class || props.blueprintName || ''
  originalClassName.value = rawClass

  // Show friendly format (replace underscores with spaces and capitalize)
  editedClassName.value = rawClass.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  nextTick(() => {
    if (classInput.value) {
      classInput.value.focus()
      classInput.value.select()
    }
  })
}

function saveClassEdit() {
  const trimmedInput = editedClassName.value.trim()

  if (!trimmedInput) {
    isEditingClass.value = false
    return
  }

  // Convert to lowercase and replace spaces with underscores
  const finalValue = trimmedInput.toLowerCase().replace(/\s+/g, '_')

  // Only update schema and mark as edited if the class actually changed
  if (finalValue !== originalClassName.value) {
    const updatedSchema = {
      ...props.blueprintSchema,
      class: finalValue,
    }

    props.setBlueprintSchema(updatedSchema)
    isSchemaEdited.value = true
    console.log('DocumentPreview: Class name changed from', originalClassName.value, 'to', finalValue)
  } else {
    console.log('DocumentPreview: Class name unchanged, not marking as edited')
  }

  isEditingClass.value = false
}

// Display name editing functions
function startEditingDisplayName() {
  isEditingDisplayName.value = true
  editedDisplayName.value = displayName.value || props.blueprintName
  nextTick(() => {
    if (displayNameInput.value) {
      displayNameInput.value.focus()
      displayNameInput.value.select()
    }
  })
}

function saveDisplayNameEdit() {
  const trimmedInput = editedDisplayName.value.trim()
  if (trimmedInput) {
    displayName.value = trimmedInput
    emit('displayNameChange', trimmedInput)
  }
  isEditingDisplayName.value = false
}

function cancelDisplayNameEdit() {
  isEditingDisplayName.value = false
  editedDisplayName.value = displayName.value || props.blueprintName
}

function cancelClassEdit() {
  isEditingClass.value = false
  editedClassName.value = ''
  originalClassName.value = ''
}

function focusField(fieldKey) {
  selectedFieldKey.value = fieldKey

  const raw = explainabilityInfo.value?.[0]
  if (!raw || !raw[fieldKey]) return

  const geometries = raw[fieldKey].geometry || []
  const match = geometries.find((g) => g.page) // just pick first available page

  if (match) {
    const targetPageIndex = match.page - 1
    if (currentPage.value !== targetPageIndex) {
      currentPage.value = targetPageIndex
      selectedDocument.value = targetPageIndex
    }
  }
}

function handlePopupOpen(isFromEdit = false) {
  if (isFromEdit) {
    popupMessage.value = t('documentPreview.extractionConfigUpdated')
    // For edit case, no pending action - just show success and stay on screen
    pendingAction = null
  } else {
    popupMessage.value = t('documentPreview.documentTrainedSuccessfully')
    // For next case, set pending action to move to next step
    pendingAction = () => {
      emit('complete', storedInvocationArn.value)
    }
  }
  popupOpen.value = true
}

function closePopup() {
  popupOpen.value = false
  // Only move to next step if this was triggered by 'Next' button (not edit)
  // If isSchemaEdited is false, it means we just completed an edit update and should stay
  if (pendingAction && typeof pendingAction === 'function') {
    pendingAction()
    pendingAction = null
  }
}
function closeErrorPopup() {
  errorPopupOpen.value = false
}

function resetFieldsFromSchema(schema) {
  if (!schema || !schema.properties) {
    return JSON.stringify([])
  }

  let schema2 = Object.entries(schema.properties).map(([key, value]) => ({
    key,
    ...value,
    isEditing: false,
    displayKey: key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()), // Capitalize each word
  }))
  return JSON.stringify(schema2)
}
async function handleYes() {
  yesNoPopupOpen.value = false

  // If schema has been edited, it means we already processed in handleEditAndFinish
  // Just emit complete to move to next step
  if (isSchemaEdited.value) {
    emit('complete', storedInvocationArn.value)
    return
  }

  // Check if we already have an invocation ARN from the initial upload (create flow)
  const existingInvocationArn = props.finalResult?.invocationArn || storedInvocationArn.value

  // For "Next" case (no schema edits), always use existing invocation if available
  if (!isSchemaEdited.value && existingInvocationArn) {
    console.log('Next flow with no edits - using existing invocation ARN:', existingInvocationArn)
    emit('complete', existingInvocationArn)
    return
  }

  // In edit mode with no changes, just proceed to next step without invoking BDA
  if (props.isEditMode && !isSchemaEdited.value) {
    console.log('Edit mode with no changes - proceeding to next step without BDA invocation')
    emit('complete', existingInvocationArn || null)
    return
  }

  // Only invoke BDA if we're in edit mode AND schema was edited, or if no existing invocation exists in create mode
  if (props.isEditMode && isSchemaEdited.value) {
    console.log('Edit mode with schema changes - invoking BDA')
  } else if (!props.isEditMode && !existingInvocationArn) {
    console.log('Create mode - No existing invocation ARN - invoking BDA')
  } else {
    console.log('Next flow - no BDA invocation needed')
    emit('complete', existingInvocationArn)
    return
  }

  isLoading.value = true

  if (!props.blueprintSchema) {
    isLoading.value = false
    return
  }

  let finalInvocationArn = null

  try {
    // Invoke BDA to process the document
    messages.value = t('documentPreview.processingDocument') || 'Processing document...'

    const invokePayload = {
      s3Url: props?.s3DocumentUrl?.s3Url,
      projectArn: projectArn,
      blueprintArn: props.blueprintArn,
      tenantId: Number(route.query.tenant_id),
      stage: 'TEST',
    }

    const finalInvokeRes = await post(`/v1/bedrock/invoke-bda`, invokePayload)
    const invokeData = finalInvokeRes.data
    finalInvocationArn = invokeData.invocationArn

    // Store at component level
    storedInvocationArn.value = finalInvocationArn

    // Save the invocation ARN to the tenant document type if document_type_id exists
    if (route.query.document_type_id) {
      const tenantDocumentTypeRes = await get(`/v1/tenant-document-types/${route.query.document_type_id}`)
      if (tenantDocumentTypeRes.data && tenantDocumentTypeRes.data.data) {
        tenantDocumentTypeRes.data.data = {
          ...tenantDocumentTypeRes.data.data,
          bda_blueprint: props.blueprintArn,
          bedrock_class_name: props.blueprintSchema?.class || props.blueprintName,
          bda_invocation_arn: finalInvocationArn,
        }
        await put(`/v1/tenant-document-types/${route.query.document_type_id}`, tenantDocumentTypeRes.data.data)
      }
    }

    // Poll for completion (optional, can be done in background)
    await pollForStatus(finalInvocationArn)
  } catch (error) {
    showErrorPopup(`Failed to process document: ${error.message}`, 'Error')
    isLoading.value = false
    return
  }

  isLoading.value = false
  // Show success popup for next case - will move to next step after popup closes
  handlePopupOpen(false)
}
function handleNo() {
  yesNoPopupOpen.value = false
}

function setZoomLevel(val) {
  zoomLevel.value = val
}

function prevPage() {
  if (currentPage.value > 0) {
    currentPage.value--
    selectedDocument.value = currentPage.value
    selectedFieldKey.value = null
  }
}

function nextPage() {
  if (currentPage.value < totalPages.value - 1) {
    currentPage.value++
    selectedDocument.value = currentPage.value
    selectedFieldKey.value = null
  }
}
function setSelectedDocument(index) {
  currentPage.value = index
  selectedDocument.value = index
  selectedFieldKey.value = null
}

const imagePages = computed(() => props.docArray.map((doc) => doc.imageUrl))
const currentImageSrc = computed(() => imagePages.value[currentPage.value])
// const pageBoundingBoxes = computed(() => {
//   const rawExplainability = toRaw(explainabilityInfo.value?.[0]);
//   if (!rawExplainability) return [];

//   if (selectedFieldKey.value && rawExplainability[selectedFieldKey.value]) {
//     const geometries = rawExplainability[selectedFieldKey.value].geometry || [];
//     return geometries
//       .filter((g) => g.page === currentPage.value + 1)
//       .map((g) => ({
//         BoundingBox: g.boundingBox,
//         fieldKey: selectedFieldKey.value,
//       }));
//   }

//   return Object.entries(rawExplainability).flatMap(([fieldKey, fieldData]) => {
//     const geometries = fieldData.geometry || [];
//     return geometries
//       .filter((g) => g.page === currentPage.value + 1)
//       .map((g) => ({
//         BoundingBox: g.boundingBox,
//         fieldKey,
//       }));
//   });
// });
const pageBoundingBoxes = computed(() => {
  const rawExplainability = toRaw(explainabilityInfo.value?.[0])

  if (!rawExplainability) {
    return []
  }

  const page = currentPage.value + 1

  if (selectedFieldKey.value && rawExplainability[selectedFieldKey.value]) {
    const geometries = rawExplainability[selectedFieldKey.value].geometry || []
    const result = geometries
      .filter((g) => g.page === page)
      .map((g) => ({
        BoundingBox: g.boundingBox,
        fieldKey: selectedFieldKey.value,
      }))
    console.log('📍 Highlighting field:', selectedFieldKey.value, 'Page:', page, 'Boxes:', result.length)
    return result
  }

  // Show all bounding boxes if no field is focused
  const result = Object.entries(rawExplainability).flatMap(([fieldKey, fieldData]) => {
    const geometries = fieldData.geometry || []
    return geometries
      .filter((g) => g.page === page)
      .map((g) => ({
        BoundingBox: g.boundingBox,
        fieldKey,
      }))
  })
  console.log('📍 Highlighting all fields on page:', page, 'Total boxes:', result.length)
  return result
})
const pageVertices = computed(() => {
  const rawExplainability = toRaw(explainabilityInfo.value?.[0])
  if (!rawExplainability) return []

  const mapGeometryToVertices = (geometry, fieldKey) => geometry.filter((g) => g.page === currentPage.value + 1 && g.vertices?.length === 4).map((g) => ({ vertices: g.vertices, fieldKey }))

  if (selectedFieldKey.value && rawExplainability[selectedFieldKey.value]) {
    return mapGeometryToVertices(rawExplainability[selectedFieldKey.value].geometry || [], selectedFieldKey.value)
  }

  return Object.entries(rawExplainability).flatMap(([fieldKey, fieldData]) => mapGeometryToVertices(fieldData.geometry || [], fieldKey))
})

const totalPages = computed(() => imagePages.value.length)

function getTableColumns(table) {
  // Get unique columns for this specific table
  if (!table?.lineItems || table.lineItems.length === 0) {
    return []
  }
  // Use first line item's fields as column headers
  return Object.keys(table.lineItems[0].fields || {})
}

function formatColumnName(columnName) {
  // Convert snake_case to Title Case
  return columnName
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

function highlightLineItemField(item, columnName) {
  selectedLineItemField.value = {
    lineNumber: item.lineNumber,
    columnName: columnName,
  }

  // Get the bounding box for this field
  const fieldData = item.fields[columnName]
  if (fieldData && fieldData.boundingBox) {
    const page = fieldData.page || 1
    // Jump to the page where this field is located
    if (currentPage.value !== page - 1) {
      currentPage.value = page - 1
      selectedDocument.value = page - 1
    }

    // Highlight the specific field
    const boundingBox = fieldData.boundingBox
    const normalizedBoundingBox = {
      left: boundingBox.Left ?? boundingBox.left ?? 0,
      top: boundingBox.Top ?? boundingBox.top ?? 0,
      width: boundingBox.Width ?? boundingBox.width ?? 0,
      height: boundingBox.Height ?? boundingBox.height ?? 0,
    }

    // Create a unique key for this field in the line item context
    const fieldKey = `line_item_${item.lineNumber}_${columnName}`

    // Update explainability info to highlight this field
    if (explainabilityInfo.value[0]) {
      if (!explainabilityInfo.value[0][fieldKey]) {
        explainabilityInfo.value[0][fieldKey] = { geometry: [] }
      }
      explainabilityInfo.value[0][fieldKey].geometry = [
        {
          page: page,
          boundingBox: normalizedBoundingBox,
          confidence: fieldData.confidence || 0,
        },
      ]
      selectedFieldKey.value = fieldKey
    }
  }
}

function handleAddNewField() {
  let fieldKey = addField.value.fieldName?.trim()
  if (!fieldKey || !addField.value.instruction) return
  fieldKey = fieldKey.toLowerCase().replaceAll(' ', '_')
  const newFieldData = {
    type: 'string',
    value: 'Update document to extract value',
    instruction: addField.value.instruction?.trim() || '',
    confidence: 0,
    inferenceType: 'inferred',
    page: currentPage.value + 1,
    isEditing: false,
  }
  const updatedProperties = {
    ...(props.blueprintSchema.properties || {}),
    [fieldKey]: newFieldData,
  }
  const updatedSchema = {
    ...props.blueprintSchema,
    properties: updatedProperties,
  }

  props.setBlueprintSchema(updatedSchema)
  isSchemaEdited.value = true
  addField.value = {
    fieldName: '',
    instruction: '',
    value: 'Update document to get result',
  }
  showAdd.value = false
}
function handleCancelAddNewField() {
  addField.value = {
    fieldName: '',
    instruction: '',
    value: 'Update document to get result',
  }
  showAdd.value = false
}
function cleanSchemaFields(data) {
  if (!data || !data.properties) {
    return data || {}
  }

  const cleanedProperties = {}
  for (const key in data.properties) {
    const { type, inferenceType, instruction } = data.properties[key]
    cleanedProperties[key] = {
      type,
      inferenceType,
      instruction,
    }
  }
  return {
    ...data,
    // Keep existing class name if present, fallback to blueprint name
    class: data.class || props.blueprintName,
    properties: cleanedProperties,
  }
}
async function handleEditAndFinish() {
  // If no schema edits were made, show confirmation popup
  if (!isSchemaEdited.value) {
    yesNoPopupOpen.value = true
    isLoading.value = false
    return
  }

  // Schema was edited - need to update blueprint and re-invoke
  isLoading.value = true

  let blueprintData = null
  let finalInvocationArn = null // Store at function scope for access in emit
  let finalStatus = null
  let finalJson = null

  try {
    if (!props.blueprintSchema) {
      showErrorPopup(t('documentPreview.missingSchema'), 'Missing Schema')
      return
    }

    const cleanedSchema = JSON.stringify(cleanSchemaFields(props.blueprintSchema))
    const payloadPut = {
      blueprintArn: props.blueprintArn,
      schema: cleanedSchema,
      finalResult: props.finalResult,
      tenantId: Number(route.query.tenant_id),
    }
    // before clean the schema save to database
    await put(`/v1/bedrock/update-blueprint`, payloadPut)
    messages.value = t('documentPreview.savingExtractionConfig')

    // // 🔹 2. Invoke BDA
    // messages.value = 'Running extraction process...'
    try {
      const invokePayload = {
        s3Url: props?.s3DocumentUrl?.s3Url,
        projectArn: projectArn,
        blueprintArn: props.blueprintArn,
        tenantId: Number(route.query.tenant_id),
        stage: 'TEST',
      }
      const finalInvokeRes = await post(`/v1/bedrock/invoke-bda`, invokePayload)

      // if (!finalInvokeRes.ok) {
      //   const errorText = await finalInvokeRes.text()
      //   throw new Error(`Status ${finalInvokeRes.status}: ${errorText}`)
      // }

      const invokeData = finalInvokeRes.data
      finalInvocationArn = invokeData.invocationArn
      // Store at component level for later use
      storedInvocationArn.value = finalInvocationArn

      // Save the invocation ARN to the tenant document type if document_type_id exists
      if (route.query.document_type_id) {
        const tenantDocumentTypeRes = await get(`/v1/tenant-document-types/${route.query.document_type_id}`)
        if (tenantDocumentTypeRes.data && tenantDocumentTypeRes.data.data) {
          tenantDocumentTypeRes.data.data = {
            ...tenantDocumentTypeRes.data.data,
            bda_blueprint: props.blueprintArn,
            bedrock_class_name: props.blueprintSchema?.class || props.blueprintName,
            bda_invocation_arn: finalInvocationArn,
          }
          await put(`/v1/tenant-document-types/${route.query.document_type_id}`, tenantDocumentTypeRes.data.data)
        }
      }
    } catch (error) {
      showErrorPopup(`${t('documentPreview.errorInvokingBDA')} ${error.message}`, t('documentPreview.failedToInvokeBDA'))
      return
    }

    // // 🔹 3. Poll for Status
    try {
      finalStatus = await pollForStatus(finalInvocationArn)
      // if (!finalStatus || finalStatus.status !== 'Success') {
      //   messages.value = `Final extraction failed: ${finalStatus?.status || 'Unknown'}`
      //   return
      // }
    } catch (error) {
      showErrorPopup(`${t('documentPreview.errorPollingStatus')} ${error.message}`, t('documentPreview.failedToGetStatus'))
      return
    }

    // // 🔹 4. Fetch Final Output
    messages.value = t('documentPreview.retrievingFinalData')
    // if (!finalStatus?.outputUri) {
    //   showErrorPopup('Missing output URI in status response.', 'Error')
    //   return
    // }

    // const finalJsonUrl = finalStatus.outputUri.replace('/job_metadata.json', '/0/custom_output/0/result.json')

    try {
      const finalJsonRes = await get(`/v1/bedrock/get-result-output?invocationArn=${finalInvocationArn}&tenantId=${Number(route.query.tenant_id)}`)

      // if (!finalJsonRes.ok) {
      //   const errorText = await finalJsonRes.text()
      //   throw new Error(`Status ${finalJsonRes.status}: ${errorText}`)
      // }

      finalJson = await finalJsonRes.data
    } catch (error) {
      showErrorPopup(`${t('documentPreview.errorFetchingOutput')} ${error.message}`, t('documentPreview.failedToFetchOutput'))
      return
    }

    // // 🔹 5. Merge and Update Schema
    messages.value = t('documentPreview.extractionComplete')
    const finalExtractedData = await mergeExplainabilityInfo(props.blueprintSchema, finalJson?.explainability_info?.[0])
    props.setBlueprintSchema(finalExtractedData)
    props.setFinalResult(finalJson)

    // Reset the edit flag after successful update
    isSchemaEdited.value = false

    // Show success popup for edit case - stay on same screen for review
    handlePopupOpen(true)

    // User stays on same screen to review the updated extraction
  } catch (error) {
    showErrorPopup(error.message, t('documentPreview.unexpectedError'))
  } finally {
    isLoading.value = false
  }
}

function formatClassName(name) {
  return name.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
}

function onBlueprintUpdate(updatedSchema) {
  props.setBlueprintSchema(updatedSchema)
  // Only mark as edited for Bedrock (which requires BDA re-invocation)
  // For Textract, don't mark as edited since we can't update blueprint on AWS
  if (!props.selectedTenantUsesTextract) {
    isSchemaEdited.value = true
  }
  console.log('DocumentPreview: Blueprint fields updated', { isTextract: props.selectedTenantUsesTextract, marked: !props.selectedTenantUsesTextract })
}
const iframeStyle = computed(() => ({
  width: '100%',
  height: '100%',
  border: 'none',
  transform: `scale(${zoomLevel.value / 100})`,
  transformOrigin: 'top center',
}))
const imgStyle = computed(() => ({
  width: '600px',
  height: '800px',
  objectFit: 'contain',
  border: '1px solid #ccc',
  transform: `scale(${zoomLevel.value / 100})`,
  transformOrigin: 'top center',
}))
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@400;500;600;700&display=swap');

.doc-page-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px;
  margin: 8px;
  border: 1px solid #ccc;
  border-radius: 6px;
  cursor: pointer;
  transition: border 0.2s;
}

.doc-page-card.selected {
  border: 2px solid var(--primary-color);
  background: #e3f2fd;
}

.doc-page-thumbnail {
  width: 80px;
  height: auto;
  object-fit: contain;
  margin-bottom: 4px;
  border-radius: 4px;
  box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
}

.doc-page-caption {
  font-size: 12px;
  color: #333;
  text-align: center;
}

.document-preview-root {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: var(--secondary-color);
  border-radius: 8px;
}

/* Add New Field Button - Underline style */
.add-btn-underline {
  background: none;
  border: none;
  color: var(--primary-color);
  font-family: 'Archivo', Arial, sans-serif;
  font-size: 0.84rem;
  font-weight: 500;
  padding: 2px 0 0 0;
  border-radius: 0;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  transition: border-bottom 0.15s, color 0.15s;
  text-decoration: underline;
  text-underline-offset: 4px;
  min-width: 0;
  width: fit-content;
  box-shadow: none;
}

.add-btn-underline:hover,
.add-btn-underline:focus {
  color: #1256a7;
  border-bottom: 2px solid #1976d2;
  background: none;
  text-decoration: underline;
}

.add-btn {
  background: none !important;
  color: inherit !important;
  border: none !important;
  box-shadow: none !important;
  padding: 0 !important;
  margin: 0 !important;
  font: inherit !important;
  font-family: 'Archivo', Arial, sans-serif;
  outline: none !important;
}

/* Match Add Form UI to Edit UI */
.add-form-card.match-edit-ui {
  background: var(--secondary-color);
  min-height: 140px;
  box-shadow: 0 2px 12px 0 #b6d7f333;
  border: 1.5px solid var(--primary-color);
  padding: 8px 26px 12px 16px;
  /* width: 90%; */
  position: relative;
}

.add-form-label {
  font-size: 12.2px;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  margin-top: 4px;
  color: var(--primary-color);
}

/* .add-form-input {
  font-size: 12.5px;
  font-family: 'Archivo', Arial, sans-serif;
  padding: 7px 8px;
  border: 1px solid #c2c8cc;
  border-radius: 6px;
  background: #f9fafd;
  outline: none;
  width: 100%;
  resize: vertical;
  transition: border 0.12s;
}

.add-form-input:focus {
  border-color: #1976d2;
} */

.add-form-actions {
  display: flex;
  gap: 6px;
}

.add-form-actions-bottom {
  justify-content: flex-end;
  margin-top: 6px;
}

/* Header Row */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 18px;
  border-bottom: 1px solid #e0e0e0;
  background: #fff;
  gap: 16px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.header-label {
  font-size: 13px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #757575;
}

.header-value {
  font-size: 15px;
  font-family: 'Archivo', Arial, sans-serif;
  text-transform: capitalize;
  line-height: 1;
  font-weight: 500;
}

.vertical-divider {
  width: 1px;
  height: 32px;
  background: #e0e0e0;
  margin: 0 10px;
}

.blueprint-btn {
  padding: 8px 12px;
  font-size: 0.93rem;
  font-family: 'Archivo', Arial, sans-serif;
  min-height: unset;
  line-height: 1;
  background: var(--primary-color);
  color: #fff;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.blueprint-btn:hover {
  background: var(--primary-color);
}

/* Panels */
.panel-group {
  display: flex;
  flex: 1 1 auto;
  min-height: 0;
  height: 0;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.panel {
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background: #fff;
  border-right: 1px solid #e0e0e0;
  overflow: hidden;
}

.left-panel {
  width: 40%;
  min-width: 200px;
  max-width: 400px;
  padding: 0px 10px 0 18px;
  border-right: 1px solid #ddd;
  overflow-y: auto;
  border-bottom-left-radius: 8px;
}

.center-panel {
  width: 80%;
  min-width: 360px;
  background: #f9f9f9;
  position: relative;
  padding: 0px 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.right-panel {
  width: 20%;
  min-width: 80px;
  background: #fcfcfc;
  padding: 0px 6px 0 6px;
  border-right: none;
  overflow-y: auto;
  border-bottom-right-radius: 8px;
}

.panel.right-panel {
  overflow-y: auto;
  max-height: 100vh;
}

/* Panel Header */
.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 10px;
}

.panel-title {
  font-size: 1.05rem;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  color: #2d2d2d;
  margin-top: 10px;
  margin-bottom: 10px;
}

.add-btn {
  background: #1976d2;
  color: #fff;
  border-radius: 5px;
  border: none;
  font-size: 0.9rem;
  font-family: 'Archivo', Arial, sans-serif;
  padding: 4px 10px;
  cursor: pointer;
  transition: background 0.2s;
}

.add-btn:hover {
  background: #478de0;
}

/* Add New Field Form */
.add-form-card {
  background: #f6f8fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 14px 12px 12px 12px;
  margin-bottom: 12px;
  position: relative;
}

.add-form-actions {
  position: absolute;
  top: 0px;
  right: 8px;
  display: flex;
  gap: 3px;
}

.add-form-label {
  font-size: 12px;
  font-weight: 600;
  font-family: 'Archivo', Arial, sans-serif;
  margin-top: 2px;
}

.add-form-input {
  font-size: 12.5px;
  font-family: 'Archivo', Arial, sans-serif;
  padding: 7px 8px;
  border: 1px solid #c2c8cc;
  border-radius: 6px;
  background: #f9fafd;
  outline: none;
  width: 100%;
  resize: vertical;
  transition: border 0.12s;
}

.add-form-input:focus {
  border-color: #164863;
}

.icon-btn {
  background: none;
  border: none;
  font-size: 1.2rem;
  font-family: 'Archivo', Arial, sans-serif;
  cursor: pointer;
  border-radius: 5px;
  padding: 4px 6px;
  color: #546e7a;
  transition: background 0.15s, color 0.18s;
  display: flex;
  align-items: center;
}

.icon-btn:hover {
  color: #1976d2;
  background: #e3f0fa;
}

.icon {
  width: 18px;
  height: 18px;
  display: block;
}

.icon-btn:active {
  color: #1976d2;
}

/* BlueprintEditor placeholder */
.blueprint-editor-placeholder {
  margin-top: 16px;
  padding: 12px;
  border-radius: 6px;
  background: #e3e9f8;
  color: #3a3a3a;
}

/* Center Panel Document Preview */
.zoom-bar {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: transparent;
}

.doc-preview-wrapper {
  height: calc(100vh - 74px);
  display: flex;
  justify-content: center;
  align-items: flex-start;
  border: 1px solid #ddd;
  background: #fff;
  overflow: auto;
  margin-top: 37px;
}

.doc-preview-iframe,
.doc-preview-image {
  display: block;
  margin: auto;
}

.doc-preview-iframe {
  width: 100%;
  height: 100%;
  min-height: 480px;
}

.doc-preview-image {
  width: 600px;
  height: 800px;
  object-fit: contain;
  border: 1px solid #ccc;
}

.unsupported-preview {
  text-align: center;
  margin-top: 48px;
  color: #888;
}

.unsupported-icon {
  font-size: 64px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #bbb;
}

/* Right Panel Pages */
.doc-page-card {
  padding: 10px 0 8px 0;
  margin-bottom: 10px;
  border: 1.5px solid #e0e0e0;
  border-radius: 7px;
  background: #fafbfc;
  text-align: center;
  cursor: pointer;
  transition: border 0.2s, background 0.2s;
}

.doc-page-card.selected {
  border: 2.5px solid #1976d2;
  background: var(--secondary-color);
}

.doc-page-fileicon {
  font-size: 36px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #90a4ae;
}

.doc-page-caption {
  font-size: 13px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #424242;
  margin-top: 2px;
}

.panel-footer {
  text-align: center;
  font-size: 12px;
  font-family: 'Archivo', Arial, sans-serif;
  color: #888;
  margin-top: 16px;
}

/* Loading Spinner */
.centered {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
}

.spinner {
  border: 4px solid #e0e0e0;
  border-top: 4px solid #1976d2;
  border-radius: 50%;
  width: 42px;
  height: 42px;
  animation: spin 1s linear infinite;
  margin-bottom: 18px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.loading-message {
  font-size: 1.1rem;
  color: var(--primary-color);
  text-align: center;
  font-weight: 500;
  font-family: 'Archivo', Arial, sans-serif;
}

@media (max-width: 900px) {
  .panel-group {
    flex-direction: column;
  }

  .left-panel,
  .center-panel,
  .right-panel {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    border-right: none !important;
    border-bottom: 1px solid #e0e0e0;
  }
}

.horizontal-loader {
  width: 200px;
  height: 6px;
  background-color: #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 22px;
}

.loader-bar {
  height: 100%;
  width: 40%;
  background-color: var(--primary-color);
  animation: slide 1s linear infinite;
  border-radius: 4px;
}

.header-value {
  position: relative;
  display: flex;
  align-items: center;
}

.edit-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  margin-left: 6px;
  padding: 4px;
  border-radius: 4px;
  transition: background 0.2s;
}

.edit-icon-btn:hover {
  background: #f0f0f0;
}

.edit-icon-btn .icon {
  width: 16px;
  height: 16px;
}

.class-edit-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.class-edit-input {
  padding: 4px 8px;
  border: 1px solid #c2c8cc;
  border-radius: 4px;
  font-size: 15px;
  font-family: 'Archivo', Arial, sans-serif;
  min-width: 150px;
}

.class-edit-input:focus {
  outline: none;
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
}

.class-edit-buttons {
  display: flex;
  gap: 4px;
}

@keyframes slide {
  0% {
    transform: translateX(-100%);
  }

  50% {
    transform: translateX(50%);
  }

  100% {
    transform: translateX(200%);
  }
}

/* Tabs Container */
.tabs-container {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.tabs-header {
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e0e0e0;
  background: #fff;
  padding: 0;
}

.tab-btn {
  flex: 1;
  border: none;
  background: #fff;
  color: #757575;
  font-family: 'Archivo', Arial, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
}

.tab-btn:hover {
  color: var(--primary-color);
  background: #eeeeee;
}

.tab-btn.active {
  color: var(--primary-color);
  border-bottom-color: var(--primary-color);
  background: #fff;
}

.tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

/* Line Items Table */
.line-items-table {
  width: 100%;
  overflow-x: auto;
}

.line-items-grid {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Archivo', Arial, sans-serif;
  font-size: 13px;
}

.line-items-grid thead {
  background: #f0f0f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.line-items-grid th {
  padding: 10px 8px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border: 1px solid #e0e0e0;
  white-space: nowrap;
}

.line-items-grid td {
  padding: 10px 8px;
  border: 1px solid #e0e0e0;
  cursor: pointer;
  transition: background 0.15s;
  position: relative;
}

.line-item-row:hover td {
  background: #f9f9f9;
}

.line-item-cell {
  min-width: 80px;
}

.line-item-cell.highlighted {
  background: #fff3cd;
  border-color: #ffc107;
  font-weight: 600;
}

.line-number {
  font-weight: 600;
  color: #757575;
  text-align: center;
  background: #fafafa;
  width: 40px;
  min-width: 40px;
}

.confidence-badge {
  display: block;
  font-size: 11px;
  color: #999;
  margin-top: 2px;
  font-weight: 400;
}

.empty-state {
  padding: 20px;
  text-align: center;
  color: #999;
  font-style: italic;
}

/* Multiple Tables Support */
.tables-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 8px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  margin-bottom: 0;
}

.table-header h4 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #333;
  font-family: 'Archivo', Arial, sans-serif;
}

.table-info {
  font-size: 11px;
  color: #999;
  font-family: 'Archivo', Arial, sans-serif;
}
</style>
